import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Phone, Edit, Briefcase, Link as LinkIcon,
  FileText, Clock, Plus, X, Save, MapPin, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactDetail = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkedJobs, setLinkedJobs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [linkJobModalOpen, setLinkJobModalOpen] = useState(false);
  const [addActivityModalOpen, setAddActivityModalOpen] = useState(false);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [searchJobQuery, setSearchJobQuery] = useState('');

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    relationship: '',
    notes: ''
  });

  const [linkJobForm, setLinkJobForm] = useState({
    job_id: '',
    role: 'Primary Contact'
  });

  const [activityForm, setActivityForm] = useState({
    activity_type: 'note',
    description: ''
  });
  
  // Contact Communications
  const [contactConversations, setContactConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  useEffect(() => {
    if (contactId) {
      fetchContactDetails();
      fetchLinkedJobs();
      fetchActivities();
      fetchContactConversations();
    }
  }, [contactId]);
  
  const fetchContactConversations = async () => {
    setConversationsLoading(true);
    try {
      const response = await axios.get(`${API}/communications/customers/${contactId}/conversations?include_messages=true`);
      setContactConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching contact conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const fetchContactDetails = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}`);
      setContact(response.data);
      setEditForm({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        relationship: response.data.relationship || '',
        notes: response.data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error('Failed to load contact details');
      navigate('/contacts-new');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedJobs = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/jobs`);
      setLinkedJobs(response.data.links || []);
    } catch (error) {
      console.error('Error fetching linked jobs:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/activities`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const searchJobs = async (query) => {
    try {
      const response = await axios.get(`${API}/contacts/search/jobs?search=${query}`);
      setAvailableJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error searching jobs:', error);
    }
  };

  useEffect(() => {
    if (linkJobModalOpen && searchJobQuery) {
      const timer = setTimeout(() => {
        searchJobs(searchJobQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchJobQuery, linkJobModalOpen]);

  const handleUpdateContact = async () => {
    try {
      await axios.put(`${API}/contacts/${contactId}`, editForm);
      toast.success('Contact updated successfully');
      setIsEditing(false);
      fetchContactDetails();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    }
  };

  const handleLinkJob = async (e) => {
    e.preventDefault();
    if (!linkJobForm.job_id) {
      toast.error('Please select a job');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/jobs`, linkJobForm);
      toast.success('Job linked successfully');
      setLinkJobModalOpen(false);
      setLinkJobForm({ job_id: '', role: 'Primary Contact' });
      fetchLinkedJobs();
      fetchActivities();
    } catch (error) {
      console.error('Error linking job:', error);
      toast.error(error.response?.data?.detail || 'Failed to link job');
    }
  };

  const handleUnlinkJob = async (linkId) => {
    if (!window.confirm('Are you sure you want to unlink this job?')) return;

    try {
      await axios.delete(`${API}/contacts/${contactId}/jobs/${linkId}`);
      toast.success('Job unlinked successfully');
      fetchLinkedJobs();
      fetchActivities();
    } catch (error) {
      console.error('Error unlinking job:', error);
      toast.error(error.response?.data?.detail || 'Failed to unlink job');
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.description) {
      toast.error('Please enter a description');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/activities`, activityForm);
      toast.success('Activity logged successfully');
      setAddActivityModalOpen(false);
      setActivityForm({ activity_type: 'note', description: '' });
      fetchActivities();
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Contact...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!contact) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-muted-foreground">Contact not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/contacts-new')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Contacts
            </Button>
          </div>
          <Button
            onClick={() => setIsEditing(!isEditing)}
            variant={isEditing ? "outline" : "default"}
          >
            {isEditing ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-2" />
                Edit Contact
              </>
            )}
          </Button>
        </div>

        {/* Contact Name Card with Blue Header */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl text-white">
                  {contact.first_name} {contact.last_name}
                </CardTitle>
                {contact.relationship && (
                  <Badge className="mt-2 bg-white/20 text-white border-0">
                    {contact.relationship}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Tabs for different sections */}
        <Tabs defaultValue="info" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info">Basic Info</TabsTrigger>
            <TabsTrigger value="communications">
              <MessageSquare className="h-3 w-3 mr-1" />
              Communications
            </TabsTrigger>
            <TabsTrigger value="jobs">Linked Jobs ({linkedJobs.length})</TabsTrigger>
            <TabsTrigger value="activity">Activity Feed</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>
                  {isEditing ? 'Edit contact details' : 'View contact details'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <Input
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <Input
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={editForm.phone}
                          onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Select
                        value={editForm.relationship}
                        onValueChange={(value) => setEditForm({...editForm, relationship: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Homeowner">Homeowner</SelectItem>
                          <SelectItem value="Property Manager">Property Manager</SelectItem>
                          <SelectItem value="Tenant">Tenant</SelectItem>
                          <SelectItem value="Contractor">Contractor</SelectItem>
                          <SelectItem value="Real Estate Agent">Real Estate Agent</SelectItem>
                          <SelectItem value="Insurance Agent">Insurance Agent</SelectItem>
                          <SelectItem value="Referral Partner">Referral Partner</SelectItem>
                          <SelectItem value="Vendor">Vendor</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        rows={4}
                      />
                    </div>
                    <Button onClick={handleUpdateContact} className="bg-green-600 hover:bg-green-700">
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Email</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">{contact.email || 'N/A'}</p>
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Phone</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">{contact.phone || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Relationship</Label>
                          <p className="text-base mt-1">{contact.relationship || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">
                              {new Date(contact.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {contact.notes && (
                      <div className="mt-4 pt-4 border-t">
                        <Label className="text-muted-foreground">Notes</Label>
                        <p className="text-base mt-2 whitespace-pre-wrap">{contact.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Communications History
                    </CardTitle>
                    <CardDescription>
                      All conversations with this contact across all jobs
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => navigate(`/communications?customer=${contactId}`)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Conversation
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {conversationsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : contactConversations.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                    <p className="text-muted-foreground mb-4">No conversations yet</p>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/communications?customer=${contactId}`)}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Start Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactConversations.map((conv) => (
                      <Card 
                        key={conv.id} 
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => navigate(`/communications?conversation=${conv.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className={`text-xs ${
                                  conv.bucket === 'lead_intake' ? 'border-blue-500 text-blue-600' :
                                  conv.bucket === 'production' ? 'border-orange-500 text-orange-600' :
                                  conv.bucket === 'service' ? 'border-purple-500 text-purple-600' :
                                  'border-gray-500 text-gray-600'
                                }`}>
                                  {conv.bucket || 'general'}
                                </Badge>
                                <Badge variant={conv.status === 'open' ? 'default' : 'secondary'} className="text-xs">
                                  {conv.status}
                                </Badge>
                                {conv.unread_count > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {conv.unread_count} unread
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Recent messages preview */}
                              {conv.recent_messages && conv.recent_messages.length > 0 && (
                                <div className="space-y-2">
                                  {conv.recent_messages.slice(-2).map((msg) => (
                                    <div 
                                      key={msg.id}
                                      className={`text-sm p-2 rounded ${
                                        msg.direction === 'inbound' 
                                          ? 'bg-muted' 
                                          : 'bg-primary/10'
                                      }`}
                                    >
                                      <span className="text-xs text-muted-foreground">
                                        {msg.direction === 'inbound' ? 'Customer' : 'Staff'} • {msg.channel}
                                      </span>
                                      <p className="truncate">{msg.body}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div className="text-right text-xs text-muted-foreground">
                              {conv.last_message_at && (
                                <span>{new Date(conv.last_message_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Linked Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Linked Jobs</CardTitle>
                    <CardDescription>
                      Jobs associated with this contact
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setLinkJobModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link Job
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {linkedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No linked jobs yet</p>
                    <Button
                      variant="outline"
                      onClick={() => setLinkJobModalOpen(true)}
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Link First Job
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {linkedJobs.map((link) => (
                      <Card key={link.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">
                                  {link.job?.job_name || link.job?.job_number || 'Untitled Job'}
                                </h4>
                                <Badge variant="outline">{link.role}</Badge>
                              </div>
                              {link.job?.address && (
                                <p className="text-sm text-muted-foreground flex items-center gap-2">
                                  <MapPin className="h-3 w-3" />
                                  {link.job.address}
                                </p>
                              )}
                              {link.job?.status && (
                                <Badge className="mt-2" variant="secondary">
                                  {link.job.status}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/jobs/${link.job_id}`)}
                              >
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnlinkJob(link.id)}
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Feed Tab */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Activity Feed</CardTitle>
                    <CardDescription>
                      Timeline of interactions and updates
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setAddActivityModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Log Activity
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No activity yet</p>
                    <Button
                      variant="outline"
                      onClick={() => setAddActivityModalOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Log First Activity
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => (
                      <div key={activity.id} className="flex gap-3 pb-4 border-b last:border-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge variant="outline" className="mb-1">
                                {activity.activity_type}
                              </Badge>
                              <p className="text-sm">{activity.description}</p>
                              {activity.user_name && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  by {activity.user_name}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Files</CardTitle>
                <CardDescription>
                  Documents and attachments related to this contact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">File upload coming soon</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Link Job Modal */}
        <Dialog open={linkJobModalOpen} onOpenChange={setLinkJobModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Link Job to Contact</DialogTitle>
              <DialogDescription>
                Search and link a job to this contact
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLinkJob} className="space-y-4">
              <div>
                <Label>Search Jobs</Label>
                <Input
                  placeholder="Enter job number, address, or client name..."
                  value={searchJobQuery}
                  onChange={(e) => setSearchJobQuery(e.target.value)}
                />
              </div>
              {availableJobs.length > 0 && (
                <div>
                  <Label>Select Job</Label>
                  <Select
                    value={linkJobForm.job_id}
                    onValueChange={(value) => setLinkJobForm({...linkJobForm, job_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a job" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableJobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.job_number || job.id} - {job.address || job.client_name || 'Untitled'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Role</Label>
                <Select
                  value={linkJobForm.role}
                  onValueChange={(value) => setLinkJobForm({...linkJobForm, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Primary Contact">Primary Contact</SelectItem>
                    <SelectItem value="Secondary Contact">Secondary Contact</SelectItem>
                    <SelectItem value="Property Manager">Property Manager</SelectItem>
                    <SelectItem value="Tenant">Tenant</SelectItem>
                    <SelectItem value="Decision Maker">Decision Maker</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLinkJobModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Link Job
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Log Activity Modal */}
        <Dialog open={addActivityModalOpen} onOpenChange={setAddActivityModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Activity</DialogTitle>
              <DialogDescription>
                Record an interaction or note for this contact
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLogActivity} className="space-y-4">
              <div>
                <Label>Activity Type</Label>
                <Select
                  value={activityForm.activity_type}
                  onValueChange={(value) => setActivityForm({...activityForm, activity_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                  rows={4}
                  placeholder="Enter activity details..."
                  required
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddActivityModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Log Activity
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ContactDetail;
